/// <amd-module name="Artifacts/Package/services/PackageService" />
import * as Res from "Artifacts/Package/Resources";
import { ManualUpstreamIngestionParameters } from "Artifacts/Package/web-apis/Contracts/AnyProtocolPackagingInternal";
import {
  getAnyProtocolPackagingInternalClient,
  IAnyProtocolPackagingInternalRestClient,
} from "Artifacts/Package/web-apis/RestClient/AnyProtocolPackagingInternal";
import * as Constants from "Artifacts/Root/Constants";
import { Protocol } from "Artifacts/Root/Constants";
import { loadNonNullData } from "Artifacts/Root/helpers/DataProviderHelpers";
import { IPackage, IPackageVersion } from "Artifacts/Root/types";
import { PackageDetails, SkeletalPackageVersion } from "Artifacts/Root/types/AzureArtifacts";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";

export interface IPackageDetailsData {
  package: IPackage;
  currentVersion: IPackageVersion;
  versions: SkeletalPackageVersion[];
}

export interface IPackageService extends IVssService {
  getPackageDetailsData(
    pageContext: IVssPageContext,
    feedId: string,
    protocol: string,
    name: string,
    version?: string,
    viewId?: string,
    projectName?: string,
  ): Promise<IPackageDetailsData>;
  ingestFromUpstream(
    ingestionParameters: ManualUpstreamIngestionParameters,
    protocol: string,
    feedId: string,
    packageName: string,
    packageVersion: string,
    project?: string,
  ): Promise<void>;
}

export class PackageService extends VssService implements IPackageService {
  get IAnyProtocolPackagingInternalRestClient(): IAnyProtocolPackagingInternalRestClient {
    return getAnyProtocolPackagingInternalClient(this.pageContext);
  }

  async ingestFromUpstream(
    ingestionParameters: ManualUpstreamIngestionParameters,
    protocol: string,
    feedId: string,
    packageName: string,
    packageVersion: string,
    project?: string,
  ): Promise<void> {
    return await this.IAnyProtocolPackagingInternalRestClient.ingestFromUpstream(
      ingestionParameters,
      protocol,
      feedId,
      packageName,
      packageVersion,
      project,
    );
  }

  async getPackageDetailsData(
    pageContext: IVssPageContext,
    feedId: string,
    protocol: string,
    name: string,
    version?: string,
    viewId?: string,
    projectName?: string,
    preferRelease?: boolean,
  ): Promise<IPackageDetailsData> {
    const properties = {
      feedId: feedId + (viewId ? "@" + viewId : ""),
      projectName: projectName,
      packageIdentifier: name, // will also accept packageId
      packageVersion: version,
      protocol: protocol,
      preferRelease: preferRelease,
    };

    const packageDetailData = await loadNonNullData<PackageDetails>(
      pageContext,
      "ms.azure-artifacts.package-details-data-provider",
      properties,
      false,
      Constants.CommonConstants.FeedServiceInstanceId,
    );

    // Failure case
    if (!packageDetailData.package || !packageDetailData.packageVersion) {
      throw new Error(Res.PackageDetails_DataLoadError);
    }

    const currentVersion = {
      id: packageDetailData.packageVersion.id,
      normalizedVersion: packageDetailData.packageVersion.normalizedVersion,
      publishDate: packageDetailData.packageVersion.publishDate,
      version: packageDetailData.packageVersion.version,
      author: packageDetailData.packageVersion.author,
      dependencies: packageDetailData.packageVersion.dependencies,
      description: packageDetailData.packageVersion.description,
      sourceChain: packageDetailData.packageVersion.sourceChain,
      url: packageDetailData.packageVersion.url,
      views: packageDetailData.packageVersion.views.map((v) => v.name),
      // we should consider putting provenance in the package version rather than recalculating it every time.
      protocolMetadata: packageDetailData.packageVersion.protocolMetadata,
      directUpstreamSourceId: packageDetailData.packageVersion.directUpstreamSourceId,
      tags: packageDetailData.packageVersion.tags,
      files: packageDetailData.packageVersion.files,
      isListed: packageDetailData.packageVersion.isListed,
      isDeleted: packageDetailData.packageVersion.isDeleted,
    };

    const versionList = packageDetailData.package.versions.map((v) => {
      return {
        id: v.id,
        publishDate: v.publishDate,
        version: v.version,
        normalizedVersion: v.normalizedVersion,
        downloadCount: 0, // Not used.
        userCount: 0, // Not used.
        views: v.views.map((x) => x.name),
        description: v.packageDescription,
        directUpstreamSourceId: v.directUpstreamSourceId,
        isListed: v.isListed,
        isDeleted: v.isDeleted,
      };
    });
    return {
      package: {
        id: packageDetailData.package.id,
        name: packageDetailData.package.name,
        protocol: packageDetailData.package.protocolType as Protocol,
      },
      versions: versionList,
      currentVersion: currentVersion,
    };
  }
}

Services.add("IPackageService", { serviceFactory: PackageService });
